/** Fonts and logo for branded PDFs, fetched once on first export. */

export interface PdfAssets {
  /** Poppins TTFs as base64; missing when they couldn't be fetched (Helvetica is used instead). */
  fonts?: { regular: string; medium: string; semibold: string };
  /** The light Yeastarify logo as a PNG data URL with its pixel size. */
  logo?: { dataUrl: string; width: number; height: number };
}

const LOGO_URL = '/branding/yeastarify-logo-light.png';
const LOGO_MAX_WIDTH_PX = 560;

let pending: Promise<PdfAssets> | null = null;

async function fetchBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

async function loadFonts(): Promise<PdfAssets['fonts']> {
  const [regular, medium, semibold] = await Promise.all(
    ['Regular', 'Medium', 'SemiBold'].map((weight) => fetchBase64(`/fonts/Poppins-${weight}.ttf`))
  );
  return { regular, medium, semibold };
}

/** The logo artwork is large; scale it down so it adds little to the PDF. */
async function loadLogo(): Promise<PdfAssets['logo']> {
  const image = new Image();
  image.src = LOGO_URL;
  await image.decode();
  const scale = Math.min(1, LOGO_MAX_WIDTH_PX / image.naturalWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);
  canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
  return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
}

/** Never rejects: a part that fails to load is left out, and is retried on the next export. */
export function loadPdfAssets(): Promise<PdfAssets> {
  pending ??= Promise.all([
    loadFonts().catch(() => undefined),
    loadLogo().catch(() => undefined),
  ]).then(([fonts, logo]) => {
    if (!fonts || !logo) pending = null;
    return { fonts, logo };
  });
  return pending;
}
